import {
    observer
} from 'mobx-react-lite';
import React, { useState } from "react";
import type { ApplicationSdk } from '../../stores';
import { useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { formatDate } from '../../helper';
import { Toolbar } from 'primereact/toolbar';
import ManagerDialogStore from '../../stores/manager/dialog/ManagerDialogStore';
import ManagerStore from '../../stores/manager/ManagerStore';
import SearchField from '../../components/SearchField';
import { Paginator } from 'primereact/paginator';
import { Chip } from 'primereact/chip';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';

const DeleteConfirmationDialog = observer(({ store, onConfirm, onCancel }: { store: any, onConfirm: () => void, onCancel: () => void }) => {
    const [confirmationText, setConfirmationText] = useState('');
    const searchWord: string = 'löschen';

    const handleConfirm = () => {
        if (confirmationText.toLowerCase() === searchWord) {
            setConfirmationText('');
            onConfirm();
        }
    };

    const handleCancel = () => {
        setConfirmationText('');
        onCancel();
    }
    
    // const productName: string = store?.productDeleteConfirmation?.name || '';
    const managerName: string = `${store?.managerDeleteConfirmation?.firstname || ''} ${store?.managerDeleteConfirmation?.lastname}`;
    // const productId: number | null = store?.productDeleteConfirmation?.id || null;

    return (
        <Dialog
            visible={!!store.managerDeleteConfirmation}
            modal
            header={'Manager löschen bestätigen'}
            footer={() => (
                <div>
                    <Button
                        label="Abbrechen"
                        icon="pi pi-times"
                        className="p-button-secondary"
                        onClick={handleCancel}
                    />
                    <Button
                        label={'Löschen'}
                        icon="pi pi-check"
                        className="p-button-danger"
                        onClick={handleConfirm}
                        disabled={confirmationText.toLowerCase() !== searchWord}
                    />
                </div>
            )}
            onHide={handleCancel}
        >
            <p>Um den Manager '{managerName}' zu löschen, geben Sie bitte das Wort <strong>{'"löschen"'}</strong> in das Feld unten ein.</p>
            <InputText
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder={searchWord}
                style={{ width: '100%' }}
            />
        </Dialog>
    );
});

const CleanerManager = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    return <>
        <Button
            label="Neu"
            icon="pi pi-plus"
            aria-label="Filter"
            onClick={() => sdk.openDialog(ManagerDialogStore, {})}
            style={{ marginRight: '0.5rem' }}
        />
    </>
});

const FilterChips = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    const selectedSearch = sdk.currentStore?.searchValue || '';

    return (
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {selectedSearch && (
                <Chip
                    label={`Suche: ${selectedSearch}`}
                    removable
                    onRemove={() => sdk.currentStore.clearSearch()}
                />
            )}
        </div>
    );
});


const SearchToolbar = ({ sdk }: { sdk: ApplicationSdk }) => {
    return <>
        <Toolbar
            start={() => <CleanerManager sdk={sdk} />}
            center={() => <></>}
            end={() => <SearchField store={sdk.currentStore} placeholder={'Objektleiter durchsuchen'} />}
        />
        <FilterChips sdk={sdk} />
    </>
}

const ManagerOverview = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    useEffect(() => {
        sdk.useStore(ManagerStore);
    }, [sdk]);

    const cleaners = sdk.currentStore?.allManagers || null;

    const onPageChange = (event: any) => {
        sdk.currentStore.onPageChange(event.page + 1);
    };

    const rowClassName = (data: any) => {
        return {
            'red-row': false // later if too less trainings
        };
    };

    return <div className="card">
        <DataTable
            value={cleaners}
            emptyMessage={'Keine Objektleiter gefunden'}
            header={<SearchToolbar sdk={sdk} />}
            onSort={(event: any) => {
                sdk.currentStore.onSort(event.sortField);
                return event;
            }}
            sortOrder={sdk.currentStore?.sortOrder || 1}
            sortField={sdk.currentStore?.sortField || 'lastname'}
            rowHover={true}
            loading={!!sdk.currentStore?.managerListLoading}
            rowClassName={rowClassName}
        >
            <Column field="id" header="Id" sortable></Column>
            <Column field="firstname" header="Vorname" sortable></Column>
            <Column field="lastname" header="Nachname" sortable></Column>
            <Column field="mail" header="E-Mail"></Column>
            <Column field={'created'} header="Erstellt" sortable body={(p: any) => formatDate(p.created)}></Column>
            <Column field={'updated'} header="Aktualisiert" sortable body={(p: any) => formatDate(p.updated)}></Column>
            <Column header="" body={(m: any) => (
                <>
                    <Button
                        icon="pi pi-pencil"
                        rounded
                        text
                        aria-label="Filter"
                        onClick={() => sdk.openDialog(ManagerDialogStore, { manager_id: m.id })}
                    />
                    {/* Löschbutton */}
                    <Button
                        icon="pi pi-trash"
                        className="p-button-text p-button-danger" // Weniger markant durch "p-button-text"
                        style={{ width: '2.5rem', height: '2.5rem' }} // Kleinere Größe
                        onClick={() => {
                            sdk.currentStore.onDeleteManager(m); // Call the delete logic from the store
                        }}
                    />
                </>
            )}></Column>


        </DataTable>
        <Paginator
            first={((sdk.currentStore?.currentPage || 1) - 1) * 30}
            rows={30}
            totalRecords={sdk.currentStore?.totalCount || 1}
            onPageChange={onPageChange}
            // rowsPerPageOptions={30}
            template="PrevPageLink CurrentPageReport NextPageLink"
            currentPageReportTemplate={`Seite {currentPage} / {totalPages} (${sdk.currentStore?.totalCount || 0} Objektleiter)`}
        />
        <DeleteConfirmationDialog
            store={sdk.currentStore}
            onConfirm={() => {
                sdk.currentStore.onDeleteManagerConfirm();
            }}
            onCancel={() => sdk.currentStore.onDeleteManagerCancel()}
        />
    </div>;
});

export default ManagerOverview;